import React from 'react'
import { Box, Typography, Collapse, IconButton } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'

/**
 * Props for CollapsibleSection component
 */
interface CollapsibleSectionProps {
  /** Title displayed in the header */
  title: string
  /** Whether the section is expanded */
  expanded: boolean
  /** Callback when header is clicked */
  onToggle: () => void
  /** Content to display when expanded */
  children: React.ReactNode
  /** Optional subtitle or additional info in header */
  subtitle?: string
}

/**
 * Reusable collapsible section component
 * Provides a consistent UI for collapsible content sections
 */
export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  expanded,
  onToggle,
  children,
  subtitle,
}) => (
  <Box sx={{ mb: 2, border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f9f9f9' }}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 1,
        cursor: 'pointer',
        '&:hover': { backgroundColor: '#f0f0f0' },
      }}
      onClick={onToggle}
    >
      <IconButton size="small" sx={{ mr: 1 }}>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </IconButton>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Collapse in={expanded}>{children}</Collapse>
  </Box>
)

export default CollapsibleSection
